<template>
  <span class="dt-image-cell" :data-testid="cellTestId">
    <!-- Empty / all failed → placeholder -->
    <span
      v-if="showPlaceholder"
      class="dt-image-empty"
      :style="boxStyle"
      :class="[shapeClass]">
      <i :class="emptyIconClass"></i>
    </span>

    <!-- Preview mode → PrimeVue Image with zoom (default on, opt out with preview: false) -->
    <span
      v-else-if="column.imageConfig?.preview !== false"
      class="dt-image-preview"
      :style="boxStyle"
      :class="[shapeClass]">
      <PrimeImage
        :src="sources[0]"
        :alt="altText"
        imageClass="dt-image-thumb"
        preview
        :pt="previewPt"
        @error="onError" />
    </span>

    <!-- Plain image -->
    <img
      v-else
      :src="sources[0]"
      :alt="altText"
      loading="lazy"
      class="dt-image-thumb"
      :style="[boxStyle, { objectFit: resolvedFit }]"
      :class="[shapeClass, { 'dt-image-dense': dense }]"
      @error="onError" />
  </span>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import PrimeImage from 'primevue/image';
import { resolveImageSources, resolveImageAlt, getCellTestId } from './cellRenderers';
import type { DataTableColumn, ImageColumnConfig } from './types';

const props = withDefaults(defineProps<{
  column: DataTableColumn;
  row: Record<string, unknown>;
  rowIndex?: number;
  testIdPrefix?: string;
  dense?: boolean;
}>(), {
  rowIndex: 0,
  testIdPrefix: 'datatable',
  dense: false,
});

const hasErrored = ref(false);

const imageConfig = computed<ImageColumnConfig | undefined>(() => props.column.imageConfig);
const sources = computed<string[]>(() => resolveImageSources(props.column, props.row));
const altText = computed<string>(() => resolveImageAlt(props.column, props.row));

const showPlaceholder = computed(() => sources.value.length === 0 || hasErrored.value);

const emptyIconClass = computed(() => imageConfig.value?.emptyIcon || 'pi pi-image');

const shape = computed(() => imageConfig.value?.shape || 'rounded');
const shapeClass = computed(() => `dt-shape-${shape.value}`);

const resolvedFit = computed(() => imageConfig.value?.fit || 'cover');

const defaultSize = computed(() => props.dense ? 32 : 40);

const boxStyle = computed(() => {
  const cfg = imageConfig.value;
  const w = cfg?.width ?? defaultSize.value;
  const h = cfg?.height ?? cfg?.width ?? defaultSize.value;
  const toPx = (v: number | string): string => typeof v === 'number' ? `${v}px` : v;
  return {
    width: toPx(w),
    height: toPx(h),
    minWidth: toPx(w),
  };
});

const cellTestId = computed(() => getCellTestId(props.column, props.rowIndex, props.testIdPrefix, 'image'));

function onError(): void {
  hasErrored.value = true;
}

/*
 * `loading: lazy` na miniatura (chamado 2026-0032): mesmo com a paginação
 * recortando as linhas, uma página cheia tem mais miniatura do que cabe na
 * tela, e cada uma é uma foto de laudo em tamanho original vinda do Blob. Sem
 * o atributo o navegador baixa todas de uma vez, inclusive as que só apareceriam
 * se a pessoa rolasse.
 */
const previewPt = computed(() => ({
  image: {
    loading: 'lazy',
    class: ['dt-image-thumb', shapeClass.value, { 'dt-image-dense': props.dense }],
    style: { objectFit: resolvedFit.value, ...boxStyle.value },
  },
}));
</script>

<style scoped>
.dt-image-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}

/* Thumbnail image */
.dt-image-thumb {
  display: block;
  background: var(--p-surface-100, #f3f4f6);
  border: 1px solid var(--p-content-border-color, #e5e7eb);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
}

.dt-image-dense {
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.05);
  border-radius: 4px;
}

/* Placeholder (empty / error) */
.dt-image-empty {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--p-surface-100, #f3f4f6);
  border: 1px solid var(--p-content-border-color, #e5e7eb);
  color: var(--p-text-muted-color, #9ca3af);
  font-size: 55%;
}

.dt-image-empty i {
  font-size: inherit;
}

/* Shape variants */
.dt-shape-rounded .dt-image-thumb,
.dt-shape-rounded.dt-image-empty {
  border-radius: 6px;
}

.dt-shape-circle .dt-image-thumb,
.dt-shape-circle.dt-image-empty {
  border-radius: 50%;
}

.dt-shape-square .dt-image-thumb,
.dt-shape-square.dt-image-empty {
  border-radius: 0;
}

/* Preview hover affordance */
.dt-image-preview {
  display: inline-flex;
  cursor: zoom-in;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.dt-image-preview:hover {
  transform: scale(1.04);
}

.dt-image-preview:hover .dt-image-thumb {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}
</style>
